import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import { Card } from '../components/ui/card';
import { Sparkles, Inbox, FileText, ClipboardCheck, ShieldAlert, CheckCircle, AlarmClock, PauseCircle, Ban, ArrowRight, Trophy, PhoneCall, XCircle, Send, Plus, Heart, Pencil, Archive } from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';
import AccessCheatSheet from '../components/AccessCheatSheet';
import MonthlyTablesSection from '../components/MonthlyTablesSection';
import DashboardEvalToSessionChart from '../components/DashboardEvalToSessionChart';
import ClinicEvalStatusPanel from '../components/ClinicEvalStatusPanel';
import { QuoteSubmitDialog } from '../components/QuoteWall';
import { Dialog, DialogTrigger } from '../components/ui/dialog';
import { ThriveAppreciationDashboard } from '../components/ThriveAppreciation';
import InfoTooltip from '../components/InfoTooltip';
import Sparkline from '../components/Sparkline';
import { fmtMonYY } from '../lib/date';

// Iter 120: These now act as a fallback pool — the real rotation is fetched
// from /api/quotes/rotation (DB-backed, super-user-submittable, with optional
// seasonal expiry). If the API is empty or fails, we fall back to this seed
// so the hero never renders blank.
const FALLBACK_QUOTES = [
  { q: "Every child deserves a champion — someone who believes in the power of connection and won't give up on them.", a: "— Rita Pierson" },
  { q: "Speech therapy isn't about fixing a child. It's about unlocking their voice.", a: "— Anonymous SLP" },
  { q: "You are the bridge between what a child wants to say and the world that needs to hear it.", a: "— Thrive" },
  { q: "Progress isn't loud. It's the quiet, patient work you do session after session.", a: "— Thrive" },
  { q: "It is easier to build strong children than to repair broken adults.", a: "— Frederick Douglass" },
  { q: "You don't need to be perfect today — you just need to keep showing up.", a: "— Anonymous" },
  { q: "Behind every child who believes in themselves is a therapist who believed first.", a: "— Anonymous" },
  { q: "Communication is the beginning of understanding. You start that beginning every single session.", a: "— Anonymous" },
  { q: "A referral is a family choosing us. Return the favor with the best care they've ever seen.", a: "— Thrive Ops" },
  { q: "Small wins compound. One clear word today, a full sentence next month, a lifetime of confidence.", a: "— Thrive" },
  { q: "You cannot pour from an empty cup. Take care of yourself first, then take care of your kiddos.", a: "— Anonymous" },
  { q: "The best therapists don't just teach speech — they teach families how to celebrate every step.", a: "— Thrive" },
  { q: "Your work today is a memory a family will treasure for decades.", a: "— Thrive" },
  { q: "Excellence isn't an act — it's a habit we choose every morning.", a: "— reframed from Aristotle" },
];

const KPI_CARDS = [
  // ── Every tile deep-links to its Today's Actions section via #<key>
  // anchor. Because the tile count == today[key].length AND the Today's
  // Actions section renders today[key], tile count and detail-list length
  // are guaranteed identical regardless of future sub-filter changes.
  // (Prior bug: tiles routed to /pipeline?stage=X which used literal stage
  // match, silently missing sub-filters like Ready-for-Therapy bd>=9 or
  // Pre-Auth Pending/Pre-Auth Needed family.)
  { key: 'new_online_inquiries',     label: 'New Online Inquiries',     linkTo: '/inquiries?disposition=new', icon: Inbox,          tone: 'bg-white',       adminOnly: false },
  { key: 'inquiries_needs_followup', label: 'Follow-up (Contacted)',    linkTo: '/inquiries?disposition=contacted', icon: PhoneCall,   tone: 'bg-[#8A3A3A]/15', prominent: true, adminOnly: true },
  { key: 'paperwork_pending',        label: 'Paperwork Pending',        linkTo: '/today#paperwork_pending',        icon: FileText,       tone: 'bg-[#BAA9CA]/15', adminOnly: true },
  { key: 'paperwork_followup',       label: 'Paperwork Follow-up',      linkTo: '/today#paperwork_followup',       icon: PhoneCall,      tone: 'bg-[#B08A3A]/20', adminOnly: true },
  { key: 'paperwork_stalled',        label: 'Stalled (3+ Follow-ups)',  linkTo: '/today#paperwork_stalled',        icon: XCircle,        tone: 'bg-[#8A3A3A]/15', prominent: true, adminOnly: true },
  // Ready-for-Eval + Pre-Auth Pending are Admin-owned buckets (Admin does
  // the scheduling and pre-auth submission). Today's Actions already hides
  // these sections from OD, so match that visibility on the Dashboard to
  // avoid tiles that navigate to a hidden section (iter 61 bug tester
  // finding).
  { key: 'ready_for_eval',           label: 'Ready for Eval',           linkTo: '/today#ready_for_eval',           icon: ClipboardCheck, tone: 'bg-[#BAA9CA]/25', adminOnly: true },
  { key: 'ready_to_submit_preauth',  label: 'Submit Pre-Auth',          linkTo: '/today#ready_to_submit_preauth',  icon: Send,           tone: 'bg-[#B08A3A]/25', adminOnly: true },
  { key: 'pre_auth_pending',         label: 'Pre-Auth Pending',         linkTo: '/today#pre_auth_pending',         icon: ShieldAlert,    tone: 'bg-[#B08A3A]/20', adminOnly: true },
  { key: 'ready_for_therapy',        label: 'Ready for Therapy',        linkTo: '/today#ready_for_therapy',        icon: CheckCircle,    tone: 'bg-[#839E97]/25' },
  { key: 'eval_overdue',             label: 'Eval Overdue (>2bd)',      linkTo: '/today#eval_overdue',             icon: AlarmClock,     tone: 'bg-[#8A3A3A]/15', prominent: true },
  { key: 'waitlisted',               label: 'Waitlisted',               linkTo: '/today#waitlisted',               icon: PauseCircle,    tone: 'bg-[#D8D6E2]' },
  { key: 'on_hold',                  label: 'On Hold',                  linkTo: '/today#on_hold',                  icon: Ban,            tone: 'bg-[#8A3A3A]/15', prominent: true },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [trends, setTrends] = useState(null);
  const [refByClinic, setRefByClinic] = useState(null);
  const [evalsByClinic, setEvalsByClinic] = useState(null);
  const [startsByClinic, setStartsByClinic] = useState(null);
  const [today, setToday] = useState(null);
  const [range, setRange] = useState({ from: null, to: null });
  // Iter 191: attribution mode for the eval leaderboard. Persisted per
  // browser so the last-selected mode survives navigation.
  const [evalsViewBy, setEvalsViewBy] = useState(() => {
    try { return localStorage.getItem('dashboard_evals_view_by') || 'main_clinic'; }
    catch { return 'main_clinic'; }
  });
  useEffect(() => {
    try { localStorage.setItem('dashboard_evals_view_by', evalsViewBy); } catch {}
  }, [evalsViewBy]);

  // Translate date range into a `months` count for the dashboard endpoints
  const trendMonths = useMemo(() => {
    if (!range.from && !range.to) return 12; // "All time" default
    const to = range.to ? new Date(range.to) : new Date();
    const from = range.from ? new Date(range.from) : new Date(to.getFullYear(), 0, 1);
    return Math.max(1, (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()) + 1);
  }, [range]);
  const clinicBarMonths = Math.min(trendMonths, 6); // keep the clinic bars digestible

  useEffect(() => {
    api.get('/dashboard/kpis').then(r => setKpis(r.data));
    api.get('/dashboard/trends', { params: { months: trendMonths } }).then(r => setTrends(r.data));
    api.get('/dashboard/referrals-by-clinic', { params: { months: clinicBarMonths } }).then(r => setRefByClinic(r.data));
    api.get('/dashboard/evals-by-clinic', { params: { months: clinicBarMonths, view_by: evalsViewBy } }).then(r => setEvalsByClinic(r.data));
    api.get('/dashboard/therapy-starts-by-clinic', { params: { months: clinicBarMonths } }).then(r => setStartsByClinic(r.data));
    api.get('/dashboard/today').then(r => setToday(r.data));
  }, [trendMonths, clinicBarMonths, evalsViewBy]);

  // Merge DB-backed staff-submitted quotes with the seed fallback so the hero
  // always has something to say + reflects every quote your team has added.
  // Each pool entry carries the raw record when DB-backed so we can heart /
  // edit / retire that specific doc inline.
  const [dbQuotes, setDbQuotes] = useState([]);
  const [quoteVersion, setQuoteVersion] = useState(0);   // bump to force re-pick
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null); // null | full quote doc
  const loadQuotes = () => api.get('/quotes/rotation?limit=200')
    .then(r => setDbQuotes(r.data?.quotes || []))
    .catch(() => setDbQuotes([]));
  useEffect(() => { loadQuotes(); }, []);
  const quotePool = useMemo(() => {
    const dbShaped = dbQuotes.map(q => ({
      q: q.text,
      a: q.author_name ? `— ${q.author_name}` : '— Anonymous',
      raw: q,   // full record → id, heart_count, viewer_has_hearted, status
    }));
    const fallbackShaped = FALLBACK_QUOTES.map(q => ({ ...q, raw: null }));
    return dbShaped.length ? [...dbShaped, ...fallbackShaped] : fallbackShaped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbQuotes]);

  const quote = useMemo(() => (
    quotePool.length ? quotePool[Math.floor(Math.random() * quotePool.length)] : { q: '', a: '', raw: null }
    // quoteVersion forces a re-pick after a new quote is added
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [quotePool, quoteVersion]);

  const heartCurrentQuote = async () => {
    if (!quote.raw?.quote_id) return;
    const qid = quote.raw.quote_id;
    // Optimistic
    const wasHearted = quote.raw.viewer_has_hearted;
    setDbQuotes(qs => qs.map(q => q.quote_id === qid ? {
      ...q,
      viewer_has_hearted: !wasHearted,
      heart_count: (q.heart_count || 0) + (wasHearted ? -1 : 1),
    } : q));
    try {
      const r = await api.post(`/quotes/${qid}/heart`);
      setDbQuotes(qs => qs.map(q => q.quote_id === qid ? { ...q, ...r.data } : q));
    } catch {
      // roll back on failure
      setDbQuotes(qs => qs.map(q => q.quote_id === qid ? {
        ...q, viewer_has_hearted: wasHearted,
        heart_count: (q.heart_count || 0) + (wasHearted ? 1 : -1),
      } : q));
    }
  };

  const retireCurrentQuote = async () => {
    if (!quote.raw?.quote_id) return;
    if (!window.confirm('Retire this quote from the rotation? It stays in the database (no data lost) but stops showing.')) return;
    try {
      await api.patch(`/quotes/${quote.raw.quote_id}`, { status: 'retired' });
      await loadQuotes();
      setQuoteVersion(v => v + 1);
    } catch (e) {
      // toast handled by caller pattern; keep quiet on Dashboard
    }
  };

  if (!kpis || !trends || !refByClinic || !evalsByClinic || !startsByClinic || !today) return <div className="text-[#685B72]" data-testid="dashboard-loading">Loading dashboard…</div>;

  // OD role flag — drives the KPI-card + leaderboard-highlight logic below.
  const isOD = user?.role === 'office_director';
  const odClinicSet = new Set(user?.clinics || (user?.clinic ? [user.clinic] : []));

  // Current-month totals used by the headline KPI tiles
  const monthLabel = refByClinic.months[refByClinic.months.length - 1];
  // Referrals tile source:
  // • Org-wide roles (super/admin/HR/read-only) see the total from the
  //   referral-leaderboard endpoint so the tile and the bar chart match.
  // • ODs see kpis.new_this_month which IS clinic-scoped. The
  //   leaderboard endpoint is intentionally org-wide for benchmarking,
  //   so we can't use it for ODs (iter 333 fix — bug reported by user
  //   showing OD tile at 104 vs. their clinic's 0 evals).
  const orgWideTileRoles = ['super_user', 'admin', 'hr_payroll', 'read_only'];
  const isOrgWideTiles = orgWideTileRoles.includes(user?.role);
  const currentMonthTotal = isOrgWideTiles
    ? (refByClinic.totals[refByClinic.totals.length - 1] || 0)
    : (kpis.new_this_month || 0);
  const tileScopeLabel = isOrgWideTiles
    ? 'across all clinics'
    : `in ${user?.clinics?.[0] || user?.clinic || 'your clinic'}`;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome + quote */}
      <div className="rounded-3xl bg-gradient-to-br from-[#BAA9CA]/25 via-[#D8D6E2]/40 to-[#839E97]/15 p-8 border border-[#E8E5EC] relative overflow-hidden" data-testid="welcome-card">
        <div className="absolute right-6 top-6 opacity-20"><Sparkles size={64} className="text-[#685B72]" /></div>
        <div className="absolute right-6 bottom-6 z-10">
          <DateRangePicker value={range} onChange={setRange} testid="dash-date-range" defaultPreset="all" />
        </div>
        <div className="text-xs uppercase tracking-widest text-[#685B72] font-semibold">Good {(() => { const h = Number(new Date().toLocaleString('en-US', { hour: '2-digit', hour12: false, timeZone: 'America/Chicago' })); return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening'; })()}, {user?.name?.split(' ')[0]}</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#2D2833] mt-2 leading-tight">Every family you touch today, thrives tomorrow.</h1>
        <div className="mt-6 max-w-2xl">
          <div className="text-lg font-serif text-[#2D2833] leading-snug italic" data-testid="rotating-quote">"{quote.q}"</div>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <div className="text-xs text-[#685B72]">{quote.a}</div>

            {/* Heart / like — only for DB-backed quotes (all authenticated users) */}
            {quote.raw?.quote_id && (
              <button
                onClick={heartCurrentQuote}
                className={`inline-flex items-center gap-1 text-[11px] font-semibold transition-colors ${
                  quote.raw.viewer_has_hearted
                    ? 'text-[#8A3A3A]'
                    : 'text-[#685B72] hover:text-[#8A3A3A]'
                }`}
                title={quote.raw.hearted_by?.length ? `Hearted by: ${quote.raw.hearted_by.map(h => h.name || h.email).join(', ')}` : 'Be the first to ❤'}
                data-testid="hero-quote-heart"
              >
                <Heart size={12} fill={quote.raw.viewer_has_hearted ? '#8A3A3A' : 'none'} strokeWidth={2} />
                {quote.raw.heart_count || 0}
              </button>
            )}

            {/* Add quote — everyone */}
            <Dialog open={quoteDialogOpen && !editingQuote} onOpenChange={o => { setQuoteDialogOpen(o); if (!o) setEditingQuote(null); }}>
              <DialogTrigger asChild>
                <button
                  className="inline-flex items-center gap-1 text-[11px] text-[#685B72] hover:text-[#2D2833] hover:underline"
                  data-testid="hero-quote-add-btn"
                  title="Add a quote to the rotation"
                >
                  <Plus size={11} /> Add quote
                </button>
              </DialogTrigger>
              <QuoteSubmitDialog
                editingQuote={editingQuote}
                onDone={() => {
                  setQuoteDialogOpen(false);
                  setEditingQuote(null);
                  loadQuotes();
                  setQuoteVersion(v => v + 1);
                }}
              />
            </Dialog>

            {/* Super-user only: edit / retire the current DB-backed quote */}
            {user?.role === 'super_user' && quote.raw?.quote_id && (
              <>
                <button
                  onClick={() => { setEditingQuote(quote.raw); setQuoteDialogOpen(true); }}
                  className="inline-flex items-center gap-1 text-[11px] text-[#685B72] hover:text-[#2D2833] hover:underline"
                  data-testid="hero-quote-edit-btn"
                  title="Edit this quote"
                >
                  <Pencil size={11} /> Edit
                </button>
                <button
                  onClick={retireCurrentQuote}
                  className="inline-flex items-center gap-1 text-[11px] text-[#8A3A3A] hover:underline"
                  data-testid="hero-quote-retire-btn"
                  title="Retire from rotation (kept in DB)"
                >
                  <Archive size={11} /> Retire
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Access cheat sheet — plain-English summary of what this user can/can't do */}
      <AccessCheatSheet />

      {/* Top 3 headline KPIs — Referrals · Evals Completed · Therapy Starts
          Each shows current-month total on top with a week-over-week comparison line. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          {
            testid: 'kpi-new-month',
            bg: 'bg-[#685B72] text-white',
            subColor: 'text-white/75',
            labelColor: 'text-white/80',
            label: 'Referrals this month',
            tooltipId: 'tt-kpi-new-month',
            tooltip: "Count of clients whose Referral Received date falls in the current calendar month, across all 9 clinics. Attributed to the client's Main Clinic.",
            value: currentMonthTotal,
            monthLabel,
            thisWeek: kpis.referrals_this_week,
            lastWeek: kpis.referrals_last_week,
            spark:    kpis.weekly_series?.referrals || [],
            sparkColor: 'rgba(255,255,255,0.85)',
          },
          {
            testid: 'kpi-evals-completed',
            bg: 'bg-[#839E97] text-white',
            subColor: 'text-white/75',
            labelColor: 'text-white/80',
            label: 'Evaluations completed',
            tooltipId: 'tt-kpi-evals',
            tooltip: 'Clients whose evaluation was marked complete this calendar month (eval_completed = Yes, eval_date in current month).',
            value: kpis.evals_this_month || 0,
            monthLabel,
            thisWeek: kpis.evals_this_week,
            lastWeek: kpis.evals_last_week,
            spark:    kpis.weekly_series?.evals || [],
            sparkColor: 'rgba(255,255,255,0.85)',
          },
          {
            testid: 'kpi-therapy-started',
            bg: 'bg-white border border-[#E8E5EC]',
            subColor: 'text-[#685B72]',
            labelColor: 'text-[#685B72]/80',
            valueColor: 'text-[#2D2833]',
            label: 'Therapy started',
            tooltipId: 'tt-kpi-therapy-started',
            tooltip: 'Clients whose therapy start date falls in the current calendar month. Signals how many new families entered active care.',
            value: kpis.therapy_starts_this_month || 0,
            monthLabel,
            thisWeek: kpis.therapy_starts_this_week,
            lastWeek: kpis.therapy_starts_last_week,
            spark:    kpis.weekly_series?.therapy_starts || [],
            sparkColor: '#4A3B5F',
          },
        ].map(t => {
          const delta = (t.thisWeek || 0) - (t.lastWeek || 0);
          const trendArrow = delta > 0 ? '▲' : delta < 0 ? '▼' : '·';
          const trendColor = t.testid === 'kpi-therapy-started'
            ? (delta > 0 ? 'text-[#3B6F5D]' : delta < 0 ? 'text-[#8A3A3A]' : 'text-[#685B72]')
            : (delta > 0 ? 'text-white' : delta < 0 ? 'text-white/70' : 'text-white/70');
          return (
            <div key={t.testid} className={`rounded-2xl p-6 ${t.bg}`} data-testid={t.testid}>
              <div className={`text-xs uppercase tracking-widest font-semibold flex items-center ${t.labelColor}`}>
                {t.label}<InfoTooltip testId={t.tooltipId} text={t.tooltip} />
              </div>
              <div className={`font-serif text-5xl mt-2 leading-none ${t.valueColor || ''}`}>{t.value}</div>
              {t.spark && t.spark.length > 0 && (
                <div className="mt-2" data-testid={`${t.testid}-sparkline-wrap`}>
                  <Sparkline data={t.spark}
                              color={t.sparkColor}
                              height={28}
                              testId={`${t.testid}-sparkline`} />
                </div>
              )}
              <div className={`text-xs mt-2 ${t.subColor}`} data-testid={`${t.testid}-week`}>
                This week <span className="font-semibold">{t.thisWeek ?? 0}</span>
                <span className={`mx-1.5 ${trendColor}`}>{trendArrow} {Math.abs(delta)}</span>
                vs last week <span className="font-semibold">{t.lastWeek ?? 0}</span>
              </div>
              <div className={`text-[11px] mt-1 ${t.subColor}`}>{fmtMonYY(t.monthLabel)} · {tileScopeLabel}</div>
            </div>
          );
        })}
      </div>

      {/* At-a-glance clinic eval availability (Speech + Feeding) */}
      <ClinicEvalStatusPanel />

      {/* Actionable KPI grid — clickable, links to filtered pipeline */}
      <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="action-cards">
        <div className="mb-4">
          <h3 className="font-serif text-2xl text-[#2D2833] flex items-center">Where the work is<InfoTooltip testId="tt-where-work" text="Live count of clients sitting in each pipeline bucket right now. Tap any tile to see the exact rows in Today's Actions." /></h3>
          <div className="text-xs text-[#685B72] mt-0.5">Click any card to jump to that section in Today's Actions</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {KPI_CARDS.filter(k => !(isOD && k.adminOnly)).map(({ key, label, linkTo, icon: Icon, tone, prominent }) => {
            const count = (today[key] || []).length;
            const isRed = prominent && count > 0;
            // linkTo is always set now — no more /pipeline?stage=<X> fallback
            // so we can't drift between compute-time filter and URL match.
            const href = linkTo || `/today#${key}`;
            return (
              <Link
                key={key}
                to={href}
                className={`rounded-xl border p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${tone} ${isRed ? 'border-[#8A3A3A]/50 ring-2 ring-[#8A3A3A]/20' : 'border-[#E8E5EC]'}`}
                data-testid={`action-card-${key}`}
              >
                <div className="flex items-center justify-between">
                  <Icon size={16} className={isRed ? 'text-[#8A3A3A]' : 'text-[#685B72]'} />
                  <ArrowRight size={12} className="text-[#685B72]/60" />
                </div>
                <div className="font-serif text-3xl text-[#2D2833] mt-3">{count}</div>
                <div className="text-xs text-[#685B72] mt-1">{label}</div>
              </Link>
            );
          })}
        </div>
      </Card>

      {/* Simple pipeline flow — the ONE trend chart. Zero-months are hidden
          so a fresh install / partial-year dataset doesn't render a flat
          left edge before real data begins. */}
      <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="trends-chart">
        <div className="mb-4">
          <h3 className="font-serif text-2xl text-[#2D2833] flex items-center">Pipeline flow — last 12 months<InfoTooltip testId="tt-pipeline-flow" text="Three trend lines: monthly Referrals in (bucketed by referral date), Evals done (evals completed in the month), and Therapy starts (clients whose therapy start date falls in the month). Zero months are hidden." /></h3>
          <div className="text-xs text-[#685B72] mt-0.5">Referrals → Evals → Therapy starts. Months with zero across the board are hidden so the funnel narrative stays honest.</div>
        </div>
        <div style={{ width: '100%', height: 300, minHeight: 300, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <LineChart
              data={(trends.months || []).filter(m => (m.referrals || 0) + (m.evals_completed || 0) + (m.therapy_starts || 0) > 0)}
              margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E5EC" />
              <XAxis dataKey="month" tick={{ fill: '#685B72', fontSize: 11 }} tickFormatter={fmtMonYY} />
              <YAxis tick={{ fill: '#685B72', fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E8E5EC' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="referrals" stroke="#685B72" strokeWidth={2.5} dot={{ r: 3 }} name="Referrals in" />
              <Line type="monotone" dataKey="evals_completed" stroke="#BAA9CA" strokeWidth={2.5} dot={{ r: 3 }} name="Evals done" />
              <Line type="monotone" dataKey="therapy_starts" stroke="#839E97" strokeWidth={2.5} dot={{ r: 3 }} name="Therapy starts" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Clinic leaderboards — Referrals · Evals Completed · Therapy Started */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <LeaderboardCard
          data={refByClinic}
          title="Clinic referral leaderboard"
          subtitle="Last 6 months. Lowest performers are where marketing lift is needed."
          testId="referral-leaderboard"
          idPrefix="lb-ref"
          accent="#B08A3A"
          highlightClinics={odClinicSet}
          tooltip="Ranks the 9 clinics by referrals received in the last 6 months. A referral is attributed to the client's Main Clinic, regardless of where evals/therapy end up happening."
        />
        <LeaderboardCard
          data={evalsByClinic}
          title={evalsViewBy === 'therapist' ? 'Therapist eval leaderboard' : 'Clinic eval completed leaderboard'}
          subtitle="Last 6 months of completed evaluations — the throughput metric."
          testId="evals-leaderboard"
          idPrefix="lb-evals"
          accent="#BAA9CA"
          highlightClinics={evalsViewBy === 'therapist' ? new Set() : odClinicSet}
          tooltip={
            evalsViewBy === 'main_clinic'
              ? "Attributed to each client's Main Clinic — the cohort view. An eval physically performed at another clinic still counts here so the funnel stays cohort-consistent."
              : evalsViewBy === 'eval_clinic'
                ? "Attributed to the clinic where the eval was actually performed (looked up from the appointment matched by eval date). Falls back to Main Clinic when no appointment matches."
                : "Attributed to the performing therapist (from the appointment matched by eval date). Covering/floater therapists show up under their own name regardless of clinic."
          }
          headerRight={
            <div className="flex items-center gap-1.5" data-testid="evals-view-by-wrap">
              <label className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">View by</label>
              <select
                value={evalsViewBy}
                onChange={e => setEvalsViewBy(e.target.value)}
                className="border rounded-lg px-2 py-1 text-xs bg-white"
                data-testid="evals-view-by-select"
              >
                <option value="main_clinic">Main Clinic</option>
                <option value="eval_clinic">Eval Clinic</option>
                <option value="therapist">Therapist</option>
              </select>
            </div>
          }
        />
        <LeaderboardCard
          data={startsByClinic}
          title="Clinic therapy started leaderboard"
          subtitle="Last 6 months of therapy starts — the conversion outcome."
          testId="starts-leaderboard"
          idPrefix="lb-starts"
          accent="#839E97"
          highlightClinics={odClinicSet}
          tooltip="Ranks the 9 clinics by therapy starts in the last 6 months. Starts are attributed to the Therapy Clinic (where ongoing therapy happens), falling back to Main Clinic when Therapy Clinic is blank."
        />
      </div>

      {/* Rolling 12-month monthly breakdown — mirrors Executive BI so ops leads
          can see the same by-clinic trends without leaving the Dashboard. */}
      <InactiveBreakdown />
      <DashboardEvalToSessionChart />
      <MonthlyTablesSection />

      {/* Warm, non-intrusive closing note from Thrive Management —
          rotating gratitude messages, elegant serif treatment. */}
      <ThriveAppreciationDashboard />
    </div>
  );
}

function LeaderboardCard({ data, title, subtitle, testId, idPrefix, accent, highlightClinics, tooltip, headerRight }) {  return (
    <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid={testId}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-serif text-xl text-[#2D2833] flex items-center gap-2">
            <Trophy size={18} style={{ color: accent }} /> {title}
            {tooltip && <InfoTooltip testId={`tt-${testId}`} text={tooltip} />}
          </h3>
          <div className="text-xs text-[#685B72] mt-0.5">{subtitle}</div>
        </div>
        {headerRight}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#FAF8FB] text-[#685B72]">
              <th className="px-2 py-2 font-semibold text-center w-10">#</th>
              <th className="px-2 py-2 font-semibold text-left">{idPrefix === 'lb-evals' && title && title.startsWith('Therapist') ? 'Therapist' : 'Clinic'}</th>
              <th className="px-2 py-2 font-semibold text-center w-16">6-mo</th>
              <th className="px-2 py-2 font-semibold text-center w-16">This mo</th>
              <th className="px-2 py-2 font-semibold text-center w-14">Δ</th>
            </tr>
          </thead>
          <tbody>
            {data.clinics.map((c, idx) => {
              const prev = c.series[c.series.length - 2] || 0;
              const delta = c.latest - prev;
              const isMine = highlightClinics && highlightClinics.has(c.clinic);
              return (
                <tr
                  key={c.clinic}
                  className={`border-t border-[#E8E5EC] ${isMine ? 'bg-[#BAA9CA]/15 ring-1 ring-inset ring-[#BAA9CA]/40 font-semibold' : ''}`}
                  data-testid={`${idPrefix}-${c.clinic.replace(/\W+/g,'_')}`}
                >
                  <td className="px-2 py-2 text-center font-mono text-[#685B72]">{idx + 1}</td>
                  <td className="px-2 py-2 text-[#2D2833] font-medium truncate">
                    {c.clinic}
                    {isMine && <span className="ml-2 text-[9px] uppercase tracking-widest text-[#685B72] bg-[#BAA9CA]/25 px-1.5 py-0.5 rounded-full" data-testid="lb-mine-tag">You</span>}
                  </td>
                  <td className="px-2 py-2 text-center font-serif text-base text-[#2D2833]">{c.total}</td>
                  <td className="px-2 py-2 text-center text-[#2D2833]">{c.latest}</td>
                  <td className={`px-2 py-2 text-center font-semibold ${delta > 0 ? 'text-[#839E97]' : delta < 0 ? 'text-[#8A3A3A]' : 'text-[#685B72]'}`}>
                    {delta > 0 ? '+' : ''}{delta}
                  </td>
                </tr>
              );
            })}
            {data.clinics.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-4 text-center text-[#685B72] text-xs">No data.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

const REASON_COLORS = {
  'Lost':                 { bg: '#8A3A3A', tint: '#8A3A3A22', text: '#8A3A3A' },
  'Withdrew':             { bg: '#B08A3A', tint: '#B08A3A22', text: '#B08A3A' },
  'Not Eligible':         { bg: '#BAA9CA', tint: '#BAA9CA22', text: '#685B72' },
  'Financial/Ins Issue':  { bg: '#839E97', tint: '#839E9722', text: '#839E97' },
};
const REASON_DEFAULT =    { bg: '#685B72', tint: '#685B7222', text: '#685B72' };

function InactiveBreakdown() {
  const [d, setD] = useState(null);
  useEffect(() => {
    import('../lib/api').then(({ default: api }) => {
      api.get('/dashboard/inactive-breakdown').then(r => setD(r.data)).catch(() => setD(null));
    });
  }, []);
  if (!d || !d.total) return null;
  return (
    <Card className="rounded-2xl border-[#E8E5EC] p-5" data-testid="inactive-breakdown">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#685B72]">Inactive breakdown</div>
          <div className="font-serif text-xl text-[#2D2833] mt-0.5">{d.total} inactive client{d.total !== 1 ? 's' : ''} <span className="text-[#685B72] text-sm font-sans">· {d.clinic_scope === 'own' ? 'your clinic' : 'group-wide'}</span></div>
        </div>
        <div className="text-[11px] text-[#685B72]">Drop-off cause across the pipeline</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {d.by_reason.map(r => {
          const c = REASON_COLORS[r.reason] || REASON_DEFAULT;
          const pct = Math.round((r.count / d.total) * 100);
          return (
            <div key={r.reason} className="rounded-xl p-3 border" style={{ borderColor: '#E8E5EC', background: c.tint }} data-testid={`inactive-${r.reason.replace(/\W+/g,'_')}`}>
              <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: c.text }}>{r.reason}</div>
              <div className="flex items-baseline gap-1.5 mt-1">
                <div className="font-serif text-2xl" style={{ color: c.text }}>{r.count}</div>
                <div className="text-[11px]" style={{ color: c.text }}>· {pct}%</div>
              </div>
              <div className="h-1 mt-2 rounded-full overflow-hidden" style={{ background: '#E8E5EC' }}>
                <div className="h-full" style={{ width: `${pct}%`, background: c.bg }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

